import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const LocationMap = () => {
  const locationDetails = {
    address: "1234 Mediterranean Avenue, Culinary District, CA 90210",
    coordinates: { lat: 34.0522, lng: -118.2437 },
    parking: "Free parking available in our private lot and street parking",
    accessibility: "Wheelchair accessible entrance and restrooms",
    publicTransit: "Metro Red Line - Culinary District Station (2 blocks)",
    landmarks: ["Next to Artisan Coffee House", "Across from Central Park", "Near Culinary Institute"]
  };

  const nearbyPlaces = [
    {
      name: "Culinary District Metro Station",
      distance: "2 blocks",
      icon: "Train",
      description: "Red Line direct access"
    },
    {
      name: "Central Park",
      distance: "1 block",
      icon: "Trees",
      description: "Perfect for pre-dinner walks"
    },
    {
      name: "Artisan Coffee House",
      distance: "Next door",
      icon: "Coffee",
      description: "Great for morning meetings"
    },
    {
      name: "Culinary Institute",
      distance: "3 blocks",
      icon: "GraduationCap",
      description: "Where our chefs trained"
    }
  ];

  const transportOptions = [
    {
      type: "Driving",
      icon: "Car",
      details: "Free parking lot with 50+ spaces",
      time: "Varies by location"
    },
    {
      type: "Public Transit",
      icon: "Train",
      details: "Metro Red Line to Culinary District",
      time: "2-minute walk from station"
    },
    {
      type: "Walking",
      icon: "MapPin",
      details: "Pedestrian-friendly neighborhood",
      time: "Great for local exploration"
    },
    {
      type: "Cycling",
      icon: "Bike",
      details: "Bike racks available at entrance",
      time: "Bike-friendly streets"
    }
  ];

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-headline text-3xl font-bold text-text-primary mb-4">
          Find Us in the Heart of Culinary District
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
          Located in the vibrant Culinary District, we're easily accessible by car, public transit, or on foot. 
          Come discover why our neighborhood is the perfect setting for authentic Mediterranean dining.
        </p>
      </div>

      {/* Map and Address */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Interactive Map */}
        <div className="lg:col-span-2">
          <div className="bg-surface rounded-xl shadow-warm overflow-hidden">
            <div className="p-6 border-b border-primary-100">
              <h3 className="font-headline text-xl font-bold text-text-primary mb-2">
                Interactive Map
              </h3>
              <p className="text-text-secondary">
                Click for directions and explore our neighborhood
              </p>
            </div>
            <div className="relative h-96">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Kebab Palace Location"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${locationDetails.coordinates.lat},${locationDetails.coordinates.lng}&z=16&output=embed`}
                className="border-0"
              />
              <div className="absolute top-4 right-4">
                <a
                  href={`https://maps.google.com/?q=${locationDetails.coordinates.lat},${locationDetails.coordinates.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white shadow-warm rounded-lg p-3 flex items-center space-x-2 text-primary hover:text-primary-600 transition-organic"
                >
                  <Icon name="ExternalLink" size={16} />
                  <span className="text-sm font-medium">Open in Maps</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Address Details */}
        <div className="space-y-6">
          <div className="bg-surface rounded-xl shadow-warm p-6">
            <h3 className="font-headline text-xl font-bold text-text-primary mb-4 flex items-center">
              <Icon name="MapPin" size={24} className="text-primary mr-3" />
              Our Address
            </h3>
            <p className="text-text-primary font-medium mb-4">
              {locationDetails.address}
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Icon name="Car" size={16} className="text-accent-600 mt-1" />
                <p className="text-sm text-text-secondary">{locationDetails.parking}</p>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="Accessibility" size={16} className="text-trust-600 mt-1" />
                <p className="text-sm text-text-secondary">{locationDetails.accessibility}</p>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="Train" size={16} className="text-conversion-600 mt-1" />
                <p className="text-sm text-text-secondary">{locationDetails.publicTransit}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-3">
            <a
              href={`https://maps.google.com/?q=${locationDetails.coordinates.lat},${locationDetails.coordinates.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <Icon name="Navigation" size={20} />
              <span>Get Directions</span>
            </a>
            <a
              href={`tel:+15551234567`}
              className="w-full bg-accent text-white font-cta font-semibold px-6 py-3 rounded-lg shadow-warm hover:bg-accent-600 transition-organic flex items-center justify-center space-x-2"
            >
              <Icon name="Phone" size={20} />
              <span>Call for Directions</span>
            </a>
          </div>
        </div>
      </div>

      {/* Transportation Options */}
      <div className="bg-surface rounded-xl shadow-warm p-8">
        <h3 className="font-headline text-2xl font-bold text-text-primary mb-6 text-center">
          How to Reach Us
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {transportOptions.map((option, index) => (
            <div key={index} className="text-center p-6 bg-background rounded-lg border border-primary-100">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={option.icon} size={28} className="text-primary" />
              </div>
              <h4 className="font-cta font-semibold text-lg text-text-primary mb-2">
                {option.type}
              </h4>
              <p className="text-sm text-text-secondary mb-2">{option.details}</p>
              <p className="text-xs text-accent-600 font-medium">{option.time}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Nearby Places */}
      <div>
        <h3 className="font-headline text-2xl font-bold text-text-primary mb-6 text-center">
          Explore Our Neighborhood
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {nearbyPlaces.map((place, index) => (
            <div key={index} className="flex items-center space-x-4 p-6 bg-surface rounded-xl shadow-warm">
              <div className="bg-accent-100 p-3 rounded-lg flex-shrink-0">
                <Icon name={place.icon} size={24} className="text-accent-600" />
              </div>
              <div>
                <h4 className="font-cta font-semibold text-lg text-text-primary">
                  {place.name}
                </h4>
                <p className="text-sm text-text-secondary mb-1">{place.description}</p>
                <p className="text-xs text-primary font-medium">{place.distance} away</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Restaurant Exterior */}
      <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-headline text-2xl font-bold text-text-primary mb-4">
              Welcome to Our Home
            </h3>
            <p className="text-text-secondary mb-6">
              Our warm, inviting exterior reflects the Mediterranean hospitality you'll experience inside. 
              The traditional architecture and welcoming entrance set the stage for an authentic dining experience 
              that celebrates both heritage and modern comfort.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Accessibility" size={20} className="text-trust-600" />
                <span className="text-text-primary">Fully accessible entrance</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Car" size={20} className="text-primary" />
                <span className="text-text-primary">Valet parking available</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Wifi" size={20} className="text-accent-600" />
                <span className="text-text-primary">Free WiFi throughout</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden shadow-warm-lg">
              <Image
                src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Kebab Palace Restaurant Exterior"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-warm p-4">
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} className="text-accent fill-current" />
                <span className="font-semibold text-text-primary">4.8/5</span>
                <span className="text-sm text-text-secondary">1,200+ reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationMap;